import { IcAlert20, IcAltitude20, IcHeight20, IcSecure20 } from '@assets/icons/20x20';
import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { NotificationDatesRange } from '@components/cockpit/panels/notification/card/NotificationDatesRange';
import { TooltipComponentProps, TooltipTypeEnum } from '@components/map/tooltips/tooltip.types';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectAltitudeUnit, selectHeightSuffix, selectHeightUnit } from '@redux/settings/settings.selectors';
import { getAlertLevelColor } from '@utils/data/notification.utils';
import { getUniqueSensorShortHand } from '@utils/target.utils';
import { convertDistanceUnitFromMeter, formatUnit } from '@utils/unit.utils';
import { FormattedMessage, useIntl } from 'react-intl';

export default function StatisticPointTooltip({ data }: Readonly<TooltipComponentProps<TooltipTypeEnum.STATISTIC>>) {
  const { formatMessage } = useIntl();
  const showHeightSuffix = useAppSelector(selectHeightSuffix);
  const heightUnit = useAppSelector(selectHeightUnit);
  const altitudeUnit = useAppSelector(selectAltitudeUnit);
  const { displayId, id, reliability, startTime, endTime, maxHeight, trackIds, worstAlertLevel, altitude } = data;

  const sensors = getUniqueSensorShortHand(trackIds);

  return (
    <VStack gap={0.5} alignItems="flex-start">
      <Text textStyle="screenStd">{displayId ?? id}</Text>
      <HStack gap={1}>
        <Icon as={IcSecure20} boxSize="20px" />
        <Text textStyle="screenStd">
          <FormattedMessage id={`target.reliability.${reliability}`} />
        </Text>
      </HStack>
      <Text textStyle="screenStd">
        <NotificationDatesRange startTime={startTime} endTime={endTime} />
      </Text>
      {maxHeight !== null && (
        <HStack gap={1}>
          <Icon as={IcHeight20} boxSize="20px" />
          <Text textStyle="screenStd">
            {formatUnit(heightUnit, convertDistanceUnitFromMeter(heightUnit, maxHeight), {
              prefix: formatMessage({ id: 'global.max' }),
            })}
          </Text>
          {showHeightSuffix && (
            <Text textStyle="screenStd" color="neutral.300">
              <FormattedMessage id="target.agl" />
            </Text>
          )}
        </HStack>
      )}
      {altitude !== null && (
        <HStack gap={1}>
          <Icon as={IcAltitude20} boxSize="20px" />
          <Text textStyle="screenStd">
            {formatUnit(altitudeUnit, convertDistanceUnitFromMeter(altitudeUnit, altitude))}
          </Text>
          {showHeightSuffix && (
            <Text textStyle="screenStd" color="neutral.300">
              <FormattedMessage id="target.amsl" />
            </Text>
          )}
        </HStack>
      )}
      <Text textStyle="screenStd">{sensors.join(' ')}</Text>
      <HStack gap={1}>
        <Icon as={IcAlert20} boxSize="20px" color={getAlertLevelColor(worstAlertLevel)} />
        <Text textStyle="screenStd">
          <FormattedMessage id={`notification.criticality.${worstAlertLevel}`} />
        </Text>
      </HStack>
    </VStack>
  );
}
